import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import {
  Grid as KendoGrid,
  GridColumn as Column,
  GridPageChangeEvent,
  GridCustomHeaderCellProps,
  GridCustomCellProps,
  GridCellProps,
} from "@progress/kendo-react-grid";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { Typography } from "@progress/kendo-react-common";
import { Button } from "@progress/kendo-react-buttons";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import React, { useEffect, useState } from "react";
import {
  HeaderThElement,
  PagerTargetEvent,
} from "@progress/kendo-react-data-tools";
import IconButton from "../../components/common/IconButton";
import { FiEdit } from "react-icons/fi";
import { useLocation } from "react-router-dom";
import FormTextField from "../../components/formFields/FormTextField";
import ButtonForAll from "../../components/common/ButtonForAll";
import {
  ActiveInactiveHotelRoomCategory,
  createHotelRoomCategory,
  deleteHotelRoomCategory,
  getAllActiveHotelRoomCategory,
  getAllHotelRoomCategory,
  updateHotelRoomCategory,
} from "./services/hotel.services";
import ShadowCard from "../../components/common/ShadowCard";
import { closeDialog, openDialog } from "../../components/dialog/dialogSlice";
import { clearHotelRoomCategoryID, setHotelRoomCategoryID } from "./hotelSlice";
import AppDialog, { DialogCloseButton } from "../../components/dialog/Dialog";
import { MdDelete } from "react-icons/md";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { Switch } from "@progress/kendo-react-inputs";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { STATUSARRAY } from "../../_contstants/common";
import { requiredValidator } from "../../components/formFields/CommonValidator";

interface RoomCategoryChangeWatcherProps {
  formRenderProps: FormRenderProps;
  handleClearRoomCategoryDetails: any;
}

const RoomCategoryChangeWatcher: React.FC<RoomCategoryChangeWatcherProps> = ({
  formRenderProps,
  handleClearRoomCategoryDetails,
}) => {
  const HotelRoomCategoryDetail = useAppSelector(
    (state) => state.hotel.HotelRoomCategoryDetail,
  );

  useEffect(() => {
    handleClearRoomCategoryDetails(formRenderProps);
  }, [HotelRoomCategoryDetail]);

  return null;
};

interface PageState {
  skip: number;
  take: number;
}

const initialDataState: PageState = { skip: 0, take: 10 };

const CreateHotelRoomCategory = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const HotelID = location.state?.HotelID;
  const HotelRoomCategoryDetail = useAppSelector(
    (state) => state.hotel.HotelRoomCategoryDetail,
  );
  const [hotelPage, setHotelPage] = useState<PageState>(initialDataState);
  const [hotelPageSizeValue, setHotelPageSizeValue] = useState<
    number | string | undefined
  >();

  const pageChangeForHotel = (event: GridPageChangeEvent) => {
    const targetEvent = event.targetEvent as PagerTargetEvent;
    const take =
      targetEvent.value === "All"
        ? HotelRoomCategoryDetail?.length || event.page.take
        : event.page.take;

    if (targetEvent.value) {
      setHotelPageSizeValue(targetEvent.value);
    }
    setHotelPage({
      ...event.page,
      take,
    });
  };

  const HeaderCustomCell = (props: GridCustomHeaderCellProps) => (
    <HeaderThElement
      columnId={props.thProps?.columnId || ""}
      {...props.thProps}
      className={`table-header ${props.index === 12 ? "locked-header" : ""}`}
    >
      {props.children}
    </HeaderThElement>
  );

  const CustomCell = (props: any) => {
    return (
      <td {...props.tdProps} colSpan={1} className={"table-cell"}>
        {props.children}
      </td>
    );
  };

  const MyDataCustomCell = (props: GridCustomCellProps) => (
    <CustomCell {...props} />
  );

  const MyEditHotelCommandCell = (
    props: GridCellProps,
    formRenderProps: FormRenderProps,
  ) => {
    const handleEdit = (item: any) => {
      formRenderProps.onChange("hotel_room_category_id", {
        value: item?.hotel_room_category_id,
      });
      formRenderProps.onChange("RoomCategoryName", {
        value: item?.RoomCategoryName,
      });
      formRenderProps.onChange("MaxAdultCount", {
        value: item?.MaxAdultCount || "",
      });
      formRenderProps.onChange("IsActive", {
        value: item?.IsActive === true ? 1 : 2,
      });
    };

    const handleOpenDeleteDialog = (ID: number) => {
      dispatch(openDialog("deleteHotelRoomCategory"));
      dispatch(setHotelRoomCategoryID(ID));
    };

    return (
      <td
        className={props.className}
        style={{
          ...props.style,
          textAlign: "center",
          display: "flex",
          justifyContent: "space-evenly",
        }}
      >
        <IconButton
          type="button"
          fillMode="flat"
          size="small"
          title="Edit"
          onClick={() => handleEdit(props.dataItem)}
          icon={FiEdit}
          iconClassName="absolute-position"
          iconStyle={{ fontSize: "20px" }}
        />

        <IconButton
          type="button"
          fillMode="flat"
          size="small"
          title="Delete"
          onClick={() =>
            handleOpenDeleteDialog(props.dataItem.hotel_room_category_id)
          }
          className="color-red"
          icon={MdDelete}
          iconClassName="absolute-position"
          iconStyle={{ fontSize: "24px" }}
        />
      </td>
    );
  };

  const MyActiveInActtiveCommandCell = (props: GridCellProps) => {
    const [IsActive, setIsActive] = useState<boolean>(props.dataItem.IsActive);

    const handleActiveInActive = () => {
      setIsActive(!IsActive);
      const data = {
        ID: props.dataItem.hotel_room_category_id,
        IsActive: !IsActive,
      };
      dispatch(ActiveInactiveHotelRoomCategory(data)).then((response: any) => {
        if (response.payload?.data?.Status === 200) {
          dispatch(getAllHotelRoomCategory({ HotelID: HotelID }));
          dispatch(getAllActiveHotelRoomCategory({ HotelID: HotelID }));
        } else {
          dispatch(getAllHotelRoomCategory({ HotelID: HotelID }));
          dispatch(getAllActiveHotelRoomCategory({ HotelID: HotelID }));
        }
      });
    };

    return (
      <td style={{ textAlign: "center" }}>
        <Tooltip anchorElement="target" position="top" parentTitle={true}>
          <div title={`Set ${IsActive ? "Inactive" : "Active"}.`}>
            <Switch
              name="IsActive"
              checked={IsActive}
              onChange={handleActiveInActive}
              size="small"
            />
          </div>
        </Tooltip>
      </td>
    );
  };

  const handleClearRoomCategoryDetails = (formRenderProps: FormRenderProps) => {
    formRenderProps.onChange("hotel_room_category_id", {
      value: "",
    });
    formRenderProps.onChange("RoomCategoryName", {
      value: "",
    });
    formRenderProps.onChange("MaxAdultCount", {
      value: "",
    });
    formRenderProps.onChange("IsActive", {
      value: 1,
    });
  };

  const handleSubmit = async (values: any) => {
    try {
      if (!values?.hotel_room_category_id) {
        const insertPayload = {
          HotelID: HotelID ? HotelID : null,
          MaxAdultCount: values?.MaxAdultCount ? +values?.MaxAdultCount : 0,
          RoomCategoryName: values?.RoomCategoryName
            ? values?.RoomCategoryName
            : "",
          IsActive: values?.IsActive === 1,
        };
        const response = await dispatch(createHotelRoomCategory(insertPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(getAllHotelRoomCategory({ HotelID: HotelID }));
          dispatch(getAllActiveHotelRoomCategory({ HotelID: HotelID }));
        }
      } else {
        const updatePayload = {
          ID: values?.hotel_room_category_id
            ? values?.hotel_room_category_id
            : null,
          MaxAdultCount: values?.MaxAdultCount ? +values?.MaxAdultCount : 0,
          HotelID: HotelID ? HotelID : null,
          RoomCategoryName: values?.RoomCategoryName
            ? values?.RoomCategoryName
            : "",
          IsActive: values?.IsActive === 1,
        };
        const response = await dispatch(updateHotelRoomCategory(updatePayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(getAllHotelRoomCategory({ HotelID: HotelID }));
          dispatch(getAllActiveHotelRoomCategory({ HotelID: HotelID }));
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  return (
    <>
      <DeleteHotelRoomCategoryDialog />
      <Form
        onSubmit={handleSubmit}
        render={(formRenderProps: FormRenderProps) => (
          <FormElement>
            <ShadowCard
              style={{
                padding: 10,
                marginBottom: 10,
                marginTop: 10,
              }}
            >
              <GridLayout
                cols={[{ width: "100%" }]}
                style={{ marginBottom: 20 }}
              >
                <GridLayoutItem>
                  <Typography.h4 className="text-dark">
                    {"Hotel Room Category"}
                  </Typography.h4>
                </GridLayoutItem>
                <GridLayoutItem>
                  <GridLayout
                    style={{ marginRight: 20 }}
                    gap={{ rows: 0, cols: 10 }}
                    cols={[
                      { width: "24.8%" },
                      { width: "24.8%" },
                      { width: "24.8%" },
                      { width: "24.8%" },
                    ]}
                  >
                    <GridLayoutItem>
                      <Field
                        wrapperClassName="w-100"
                        name={`RoomCategoryName`}
                        label={`Category Name`}
                        placeholder="Room Category"
                        component={FormTextField}
                        astrike={true}
                        validator={requiredValidator}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem>
                      <Field
                        wrapperClassName="w-100"
                        name={`MaxAdultCount`}
                        label={`Total Pax Allowed`}
                        placeholder="0"
                        type="number"
                        component={FormTextField}
                        astrike={true}
                        validator={requiredValidator}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem style={{ width: "100%" }}>
                      <Field
                        name="IsActive"
                        label="Status"
                        placeholder="i.e. Active"
                        astrike={true}
                        validator={requiredValidator}
                        component={FormSelectionField}
                        options={STATUSARRAY?.map((status: any) => {
                          return {
                            value: status?.value,
                            label: status?.label,
                          };
                        })}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem style={{ textAlign: "end", marginTop: 36 }}>
                      <ButtonForAll
                        label={
                          formRenderProps.valueGetter("hotel_room_category_id")
                            ? "Update"
                            : "Add"
                        }
                        disabled={
                          formRenderProps.valueGetter("RoomCategoryName")
                            ? false
                            : true
                        }
                        type="submit"
                      />
                      <Button
                        style={{ marginLeft: 10 }}
                        fillMode={"outline"}
                        themeColor={"primary"}
                        type="button"
                        onClick={() => {
                          handleClearRoomCategoryDetails(formRenderProps);
                        }}
                      >
                        Clear
                      </Button>
                    </GridLayoutItem>
                  </GridLayout>
                </GridLayoutItem>
              </GridLayout>
              {HotelRoomCategoryDetail &&
                HotelRoomCategoryDetail?.length > 0 && (
                  <>
                    <KendoGrid
                      style={{ marginTop: 14 }}
                      data={
                        HotelRoomCategoryDetail
                          ? HotelRoomCategoryDetail?.slice(
                              hotelPage.skip,
                              hotelPage.take + hotelPage.skip,
                            )
                          : []
                      }
                      skip={hotelPage.skip}
                      take={hotelPage.take}
                      total={HotelRoomCategoryDetail?.length}
                      pageable={{
                        buttonCount: 5,
                        pageSizes: [10, 25, 50, "All"],
                        pageSizeValue: hotelPageSizeValue,
                        type: "input",
                      }}
                      onPageChange={pageChangeForHotel}
                      cells={{
                        headerCell: HeaderCustomCell,
                        data: MyDataCustomCell,
                      }}
                    >
                      <Column field="RoomCategoryName" title="Room Category" />
                      <Column
                        width={150}
                        field="MaxAdultCount"
                        title="Total Pax Allowed"
                        cells={{
                          data: (props) => (
                            <div className="text-center">
                              {props.dataItem.MaxAdultCount}
                            </div>
                          ),
                        }}
                      />
                      <Column
                        field="ID"
                        title="Status"
                        cells={{ data: MyActiveInActtiveCommandCell }}
                        width={"90px"}
                        filterable={false}
                        locked={true}
                      />
                      <Column
                        field="ID"
                        title="Actions"
                        cells={{
                          data: (props: any) =>
                            MyEditHotelCommandCell(props, formRenderProps),
                        }}
                        width={"110px"}
                        filterable={false}
                        locked={true}
                      />
                    </KendoGrid>
                  </>
                )}
            </ShadowCard>
            <RoomCategoryChangeWatcher
              formRenderProps={formRenderProps}
              handleClearRoomCategoryDetails={handleClearRoomCategoryDetails}
            />
          </FormElement>
        )}
      />
    </>
  );
};

const DeleteHotelRoomCategoryDialog: React.FC = () => {
  const dispatch = useAppDispatch();
  const dialogName = useAppSelector((state) => state.dialog.dialogName);
  const location = useLocation();
  const HotelID = location.state?.HotelID;

  const HotelRoomCategoryID = useAppSelector(
    (state) => state.hotel.HotelRoomCategoryID,
  );

  const handleDeleteAction = async (ID: number | null) => {
    if (ID) {
      const response = await dispatch(deleteHotelRoomCategory(ID));
      if (response.meta.requestStatus === "fulfilled") {
        dispatch(getAllHotelRoomCategory({ HotelID: HotelID }));
        dispatch(closeDialog());
        dispatch(clearHotelRoomCategoryID());
      } else {
        dispatch(getAllHotelRoomCategory({ HotelID: HotelID }));
        dispatch(closeDialog());
        dispatch(clearHotelRoomCategoryID());
      }
    }
  };
  return (
    <>
      {dialogName === "deleteHotelRoomCategory" && (
        <AppDialog>
          <>
            <Typography.h5>{"Delete Room Category?"}</Typography.h5>
            <GridLayout>
              <GridLayoutItem>
                <Typography.p>
                  Are you sure you want to delete this Room Category?
                </Typography.p>
              </GridLayoutItem>
              <GridLayoutItem
                style={{
                  display: "flex",
                  justifyContent: "end",
                }}
              >
                <DialogCloseButton themeColor="error" />
                <Button
                  fillMode={"solid"}
                  themeColor={"error"}
                  onClick={() => handleDeleteAction(HotelRoomCategoryID)}
                  type="button"
                >
                  Delete
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </>
        </AppDialog>
      )}
    </>
  );
};

export default CreateHotelRoomCategory;
